import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  BookOpen, 
  Code, 
  FileText, 
  HelpCircle, 
  ExternalLink, 
  Search,
  ChevronRight,
  Download,
  Languages,
  Upload,
  Settings,
  CreditCard
} from 'lucide-react'
import Card from '../components/ui/Card'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'

const DocumentationPage = () => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSection, setSelectedSection] = useState('phrase-integration') // Default to TMS integration

  const sections = [
    {
      id: 'getting-started',
      title: t('docs.gettingStarted'),
      icon: BookOpen,
      content: {
        title: t('docs.content.gettingStarted.title'),
        items: [
          {
            title: t('docs.content.gettingStarted.accountSetup.title'),
            content: t('docs.content.gettingStarted.accountSetup.content')
          },
          {
            title: t('docs.content.gettingStarted.firstTranslation.title'),
            content: t('docs.content.gettingStarted.firstTranslation.content')
          },
          {
            title: t('docs.content.gettingStarted.understandingFormats.title'),
            content: t('docs.content.gettingStarted.understandingFormats.content')
          }
        ]
      }
    },
    {
      id: 'translation-guide',
      title: t('docs.translationGuide'),
      icon: Languages,
      content: {
        title: t('docs.content.translationGuide.title'),
        items: [
          {
            title: t('docs.content.translationGuide.supportedLanguages.title'),
            content: t('docs.content.translationGuide.supportedLanguages.content')
          },
          {
            title: t('docs.content.translationGuide.filePreparation.title'),
            content: t('docs.content.translationGuide.filePreparation.content')
          },
          {
            title: t('docs.content.translationGuide.qualityReview.title'),
            content: t('docs.content.translationGuide.qualityReview.content')
          }
        ]
      }
    },
    {
      id: 'file-upload',
      title: t('docs.fileUpload'),
      icon: Upload,
      content: {
        title: t('docs.content.fileUpload.title'),
        items: [
          {
            title: t('docs.content.fileUpload.supportedFormats.title'),
            content: t('docs.content.fileUpload.supportedFormats.content')
          },
          {
            title: t('docs.content.fileUpload.batchProcessing.title'),
            content: t('docs.content.fileUpload.batchProcessing.content')
          },
          {
            title: t('docs.content.fileUpload.errorHandling.title'),
            content: t('docs.content.fileUpload.errorHandling.content')
          }
        ]
      }
    },
    {
      id: 'api-integration',
      title: t('docs.apiIntegration'),
      icon: Code,
      content: {
        title: t('docs.content.apiIntegration.title'),
        items: [
          {
            title: t('docs.content.apiIntegration.authentication.title'),
            content: t('docs.content.apiIntegration.authentication.content')
          },
          {
            title: t('docs.content.apiIntegration.jobManagement.title'),
            content: t('docs.content.apiIntegration.jobManagement.content')
          },
          {
            title: t('docs.content.apiIntegration.webhooks.title'),
            content: t('docs.content.apiIntegration.webhooks.content')
          }
        ]
      }
    },
    {
      id: 'phrase-integration',
      title: 'TMS Integration',
      icon: Settings,
      content: {
        title: 'T8R TMS Integration Guide',
        items: [
          {
            title: 'Complete Workflow Overview',
            content: 'T8R TMS integration follows a 5-step process: 1) Configure TMS Projects in Settings with Phrase API tokens, 2) Complete translation jobs on the Translation page, 3) Select completed jobs for import using the job selector, 4) Configure import settings including target TMS project and language mappings, 5) Monitor import progress with real-time status updates in the Import History section.'
          },
          {
            title: 'Smart Job Selection',
            content: 'The job selector allows filtering completed translation jobs by status, date range, and content. It supports direct job linking from translation workflows and provides search functionality by filename and job ID. Users can access this via the TMS Integration page or through direct URL parameters.'
          },
          {
            title: 'Automated XLIFF Conversion',
            content: 'T8R automatically converts from its internal translation format to XLIFF 1.2 with proper language code mapping and metadata preservation. This ensures compatibility with TMS platforms while maintaining translation quality and context information.'
          },
          {
            title: 'Multi-Project Support',
            content: 'Configure multiple TMS projects with different API tokens, allowing organized imports across different clients or brands. Each project can have its own language mappings, import settings, and access permissions for team collaboration.'
          },
          {
            title: 'Import History & Tracking',
            content: 'Comprehensive import history with pagination (10 items per page), status filtering, detailed statistics, and error reporting. The history includes duration tracking, retry counts, processed key statistics, and user-friendly import details instead of raw JSON data.'
          },
          {
            title: 'Supported TMS Platforms',
            content: 'Currently supporting Phrase with standard XLIFF 1.2 format. Additional TMS platforms can be added through the extensible import framework. The system handles file size limits, API rate limiting, and automatic retry mechanisms.'
          },
          {
            title: 'Troubleshooting Common Issues',
            content: 'Common solutions: Import Fails - Verify API token has upload permissions in your TMS project settings. Language Mismatch - Check that source/target languages in T8R match those configured in your TMS project. Permission Errors - Ensure your API token has project manager or translator permissions. File Size Limits - Large translation jobs may be split automatically to respect TMS API limits.'
          }
        ]
      }
    },
    {
      id: 'billing',
      title: t('docs.billingPlans'),
      icon: CreditCard,
      content: {
        title: t('docs.content.billing.title'),
        items: [
          {
            title: t('docs.content.billing.planComparison.title'),
            content: t('docs.content.billing.planComparison.content')
          },
          {
            title: t('docs.content.billing.usageTracking.title'),
            content: t('docs.content.billing.usageTracking.content')
          },
          {
            title: t('docs.content.billing.upgrading.title'),
            content: t('docs.content.billing.upgrading.content')
          }
        ]
      }
    }
  ]

  const faqItems = [
    {
      question: t('faq.items.0.question', 'What file formats are supported?'),
      answer: t('faq.items.0.answer', 'T8R supports CSV (.csv), Excel files (.xls, .xlsx), and JSON files (.json) up to 10MB in size. JSON files automatically detect the source language from the filename (e.g., en.json). We recommend using UTF-8 encoding for all text files.')
    },
    {
      question: t('faq.items.1.question', 'How accurate are the translations?'),
      answer: t('faq.items.1.answer', 'Our AI models provide professional-grade translations with high accuracy. The exact quality depends on the language pair and content type. We also offer quality review features to validate translations.')
    },
    {
      question: t('faq.items.2.question', 'Can I integrate T8R with my existing tools?'),
      answer: t('faq.items.2.answer', 'Yes! T8R offers REST API integration and supports Phrase TMS integration. You can also download results in standard formats for use with other tools.')
    },
    {
      question: t('faq.items.3.question', 'How is billing calculated?'),
      answer: t('faq.items.3.answer', 'Billing is based on the number of characters translated. Each plan includes a monthly character allowance, and usage is tracked in real-time in your dashboard.')
    },
    {
      question: t('faq.items.4.question', 'Is my data secure?'),
      answer: t('faq.items.4.answer', 'Absolutely. All data is encrypted in transit and at rest. We follow industry best practices for data security and never store your translations longer than necessary.')
    },
    {
      question: t('faq.items.5.question', 'Can I cancel my subscription anytime?'),
      answer: t('faq.items.5.answer', 'Yes, you can cancel your subscription at any time. You\'ll continue to have access to your plan features until the end of your current billing period.')
    }
  ]

  const selectedContent = sections.find(s => s.id === selectedSection)?.content

  const filteredFAQ = faqItems.filter(item => 
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('docs.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('docs.subtitle')}
        </p>
      </div>

      {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder={t('docs.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <Card.Header>
              <Card.Title>{t('navigation.documentation')}</Card.Title>
            </Card.Header>
            <Card.Content className="p-0">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedSection === section.id
                        ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border-r-2 border-primary-500'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{section.title}</span>
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </button>
                ))}
              </nav>
            </Card.Content>
          </Card>

          {/* Quick Links */}
          <Card className="mt-6">
            <Card.Header>
              <Card.Title>{t('docs.quickLinks')}</Card.Title>
            </Card.Header>
            <Card.Content className="space-y-3">
              <a
                href="/api/v1/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                <span>{t('docs.apiDocumentation')}</span>
              </a>
              <a
                href="mailto:support@t8r.ai"
                className="flex items-center space-x-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                <HelpCircle className="w-4 h-4" />
                <span>{t('docs.contactSupport')}</span>
              </a>
              <Button variant="outline" size="sm" className="w-full">
                <Download className="w-4 h-4 mr-2" />
                {t('docs.downloadGuide')}
              </Button>
            </Card.Content>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Content Sections */}
          {selectedContent && (
            <Card>
              <Card.Header>
                <Card.Title>{selectedContent.title}</Card.Title>
              </Card.Header>
              <Card.Content className="space-y-6">
                {selectedContent.items.map((item, index) => (
                  <div key={index}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                ))}
              </Card.Content>
            </Card>
          )}

          {/* Code Examples (for API section) */}
          {selectedSection === 'api-integration' && (
            <Card>
              <Card.Header>
                <Card.Title>{t('code.examples')}</Card.Title>
              </Card.Header>
              <Card.Content>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      {t('code.uploadJob')}
                    </h4>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-green-400 text-sm">
{`curl -X POST "https://api.t8r.ai/api/v1/user/jobs/upload" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -F "file=@your-file.json" \\
  -F "source_lang=en-US" \\
  -F "target_langs=cs-CZ" \\
  -F "target_langs=sk-SK"`}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      {t('code.checkStatus')}
                    </h4>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-green-400 text-sm">
{`curl -X GET "https://api.t8r.ai/api/v1/user/jobs/{job_id}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`}
                      </pre>
                    </div>
                  </div>
                </div>
              </Card.Content>
            </Card>
          )}

          {/* FAQ Section */}
          <Card>
            <Card.Header>
              <Card.Title>{t('faq.title')}</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="space-y-6">
                {filteredFAQ.map((item, index) => (
                  <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-b-0 last:pb-0">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {item.question}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                ))}
                
                {filteredFAQ.length === 0 && searchQuery && (
                  <div className="text-center py-8">
                    <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('faq.noResults', { query: searchQuery })}
                    </p>
                  </div>
                )}
              </div>
            </Card.Content>
          </Card>

          {/* Need More Help */}
          <Card>
            <Card.Content className="text-center py-8">
              <HelpCircle className="w-12 h-12 text-primary-600 dark:text-primary-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('help.needMore')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('help.cantFind')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button>
                  {t('help.contactSupport')}
                </Button>
                <Button variant="outline">
                  {t('help.joinCommunity')}
                </Button>
              </div>
            </Card.Content>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default DocumentationPage